import React, { lazy, Suspense } from "react";
import BsInfoCircleFill from "../../assets/icons/info";
import { isArray, parseCamelKey } from "../../utils/formatersAndParsers";
import MySelect from "../../Component/common/mySelect";
import { ErrorField } from "../../Component/common/errorField";
import { handleFile } from "../../services/handleFile";
import { errorMsg } from "../../utils/toastMessage";
import { PasswordInput, PhoneInput, TextInput } from "./components/inputFields";
import Asterik from "../common/asterik";
import { OverlayLoader } from "../Loader";
import { genderOptions } from "../../utils/constants";

const CkEditor = lazy(() => import("./components/ckEditor"));

export default function MyForm({
  values = {},
  setFieldValue = () => {},
  errors = {},
  touched = false,
  formFields = [],
  RestForm = () => <></>,
  disableFields = false,
  handleChange = () => {},
  setSubmitting = () => {},
}) {
  const handleValueChange = (name, value) => {
    setFieldValue(name, value);
  };

  const handleFileChange = async (e) => {
    const { name, files } = e.target;
    const file = files[0];

    if (file) {
      setSubmitting(true);
      const fileURL = await handleFile(file, false);
      if (!fileURL) return errorMsg("Error uploading file");

      const myFile = {
        url: isArray(fileURL)[0]?.url || "",
        name: isArray(fileURL)[0]?.filename,
      };
      setFieldValue(name, myFile);
      setSubmitting(false);
    }
  };

  return (
    <>
      {formFields?.map((field) =>
        field.type === "phone" ? (
          <div
            key={field.value}
            className={`position-relative my-2 ${field.wrapperClassName || ""}`}>
            <label htmlFor={field.value} className="text-color mb-1">
              {field.lable || parseCamelKey(field.value)}
              {field.isRequired && <Asterik />}
            </label>
            <PhoneInput
              placeholder={field.placeholder}
              name={field.value}
              value={values[field.value]}
              isDisabled={disableFields || field.disabled}
              onChange={(name, formattedValue) => {
                handleValueChange(name, formattedValue);
              }}
            />
            {(!touched || touched?.[field.value]) && (
              <ErrorField touched={touched} error={errors[field.value]} />
            )}
          </div>
        ) : field.type === "password" ? (
          <div key={field.value} className={`my-2 ${field.wrapperClassName || ""}`}>
            <div className=" d-flex align-items-center gap-3 w-100">
              <label className="text-color hstack gap-2 mb-1">
                <span htmlFor={field.value}>
                  {field.label || parseCamelKey(field.value)}
                  {field.isRequired && <Asterik />}
                </span>

                <BsInfoCircleFill
                  className="pointer"
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Use 8 or more characters with a mix of letters, numbers & symbols"
                />
              </label>
            </div>

            <PasswordInput
              placeholder={field.placeholder}
              name={field.value}
              value={values[field.value]}
              isDisabled={disableFields || field.disabled}
              onChange={handleChange}
            />
            {(!touched || touched?.[field.value]) && (
              <ErrorField touched={touched} error={errors[field.value]} />
            )}
          </div>
        ) : field.type === "select" ? (
          <div
            key={field.value}
            className={`position-relative my-2 ${field.wrapperClassName || ""}`}>
            <label htmlFor={field.value} className="text-color mb-1">
              {field.label || parseCamelKey(field.value)}
              {field.isRequired && <Asterik />}
            </label>
            <MySelect
              placeholder={`Select ${field.value}`}
              isDisabled={disableFields || field.disabled}
              options={field.options}
              value={
                field.value === "gender"
                  ? genderOptions.find((gender) => gender.value === values[field.value]) || ""
                  : values[field.value] || ""
              }
              onChange={(selected) => {
                handleValueChange(field.value, selected);
              }}
            />

            {(!touched || touched?.[field.value]) && (
              <ErrorField touched={touched} error={errors[field.value]} />
            )}
          </div>
        ) : field.type === "checkbox" ? (
          <div
            key={field.value}
            className={`position-relative my-2 ${field.wrapperClassName || ""}`}>
            <input
              type="checkbox"
              id={field.value}
              className="rememberMe me-1 shadow-none"
              name={field.value}
              checked={values[field.value]}
              onChange={(e) => handleValueChange(field.value, e.target.checked)}
              disabled={disableFields}
            />
            <label htmlFor={field.value} className="text-color mb-1">
              {field.label || parseCamelKey(field.value)}
              {field.isRequired && <Asterik />}
            </label>
          </div>
        ) : field.type === "file" ? (
          <div
            key={field.value}
            className={`position-relative my-2 ${field.wrapperClassName || ""}`}>
            <label htmlFor={field.value} className="text-color mb-1">
              {field.label || parseCamelKey(field.value)}
              {field.isRequired && <Asterik />}
            </label>
            <input
              type="file"
              id={field.value}
              className="form-control shadow-none"
              name={field.value}
              onChange={handleFileChange}
            />
            {(!touched || touched?.[field.value]) && (
              <ErrorField touched={touched} error={errors[field.value]} />
            )}
          </div>
        ) : ["text", "email", "number", "date"].includes(field.type) ? (
          <div
            key={field.value}
            className={`position-relative my-2 ${field.wrapperClassName || ""}`}>
            <label htmlFor={field.value} className="text-color mb-1 hstack gap-2">
              <span>
                {field.label || parseCamelKey(field.value)}
                {field.isRequired && <Asterik />}
              </span>
              {field.value === "bio" && (
                <BsInfoCircleFill
                  className="pointer"
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Write something about yourself. This will be visible on your profile page and when someone search your profile on google."
                />
              )}
            </label>

            <TextInput
              type={field.type || "text"}
              placeholder={field.placeholder || parseCamelKey(field.value)}
              isDisabled={disableFields || field.disabled}
              name={field.value}
              value={values[field.value]}
              onChange={handleChange}
            />
            {(!touched || touched?.[field.value]) && (
              <ErrorField touched={touched} error={errors[field.value]} />
            )}
          </div>
        ) : field.type === "textarea" ? (
          <div
            key={field.value}
            className={`position-relative my-2 ${field.wrapperClassName || ""}`}>
            <label htmlFor={field.value} className="text-color mb-1">
              {field.label || parseCamelKey(field.value)}
              {field.isRequired && <Asterik />}
            </label>
            <textarea
              className="form-control"
              placeholder={field.placeholder || parseCamelKey(field.value)}
              disabled={disableFields || field.disabled}
              name={field.value}
              value={values[field.value]}
              onChange={handleChange}
            />
            {(!touched || touched?.[field.value]) && (
              <ErrorField touched={touched} error={errors[field.value]} />
            )}
          </div>
        ) : field.type === "ckEditor" ? (
          <div
            key={field.value}
            className={`position-relative my-2 ${field.wrapperClassName || ""}`}>
            <label htmlFor={field.value} className="text-color mb-1">
              {field.label || parseCamelKey(field.value)}
              {field.isRequired && <Asterik />}
            </label>
            <Suspense fallback={<OverlayLoader />}>
              <CkEditor
                data={field.value}
                onChange={(data) => {
                  handleValueChange(field.value, data);
                }}
              />
            </Suspense>
            {(!touched || touched?.[field.value]) && (
              <ErrorField touched={touched} error={errors[field.value]} />
            )}
          </div>
        ) : (
          ""
        )
      )}
      <RestForm />
    </>
  );
}
